/**
 * API service for Knowledge Base endpoints
 */

import { api } from './api';

export interface TaskFramework {
  required_steps?: Record<string, any>;
  validations?: Record<string, any>;
  output_format?: Record<string, any>;
  [key: string]: any;
}

export interface DomainRule {
  accounting_basis?: string;
  fiscal_year_end?: string;
  materiality_threshold?: number;
  [key: string]: any;
}

export interface SafetyCheck {
  checks?: Record<string, any>;
  [key: string]: any;
}

export interface KnowledgeBaseConfig {
  id?: string;
  tenant_id?: string;
  name: string;  // Backend returns 'name' not 'config_name'
  type: string;  // Backend returns 'type' not 'config_type'
  data: TaskFramework | DomainRule | SafetyCheck | any;  // Backend returns 'data' not 'config_data'
  version?: number;
  is_active?: boolean;
  is_base_template?: boolean;
  is_system?: boolean;  // System frameworks cannot be renamed
  created_at?: string;
  modified_at?: string;
  modified_by?: string;
  // Legacy field names for backwards compatibility
  config_type?: string;
  config_name?: string;
  config_data?: TaskFramework | DomainRule | SafetyCheck;
}

export interface TenantProfile {
  id?: string;
  tenant_id: string;
  company_name?: string;
  industry?: string;
  accounting_basis?: string;
  fiscal_year_end?: string;
  settings?: Record<string, any>;
  created_at?: string;
  modified_at?: string;
}

export interface TaskHistory {
  id?: string;
  tenant_id: string;
  task_type: string;
  success_count?: number;  // Made optional to handle backend variations
  failure_count?: number;  // Made optional to handle backend variations
  execution_count?: number; // New field from backend
  last_executed?: string;   // New field from backend
  learned_patterns?: any[];
  previous_issues?: any[];
  created_at?: string;
  modified_at?: string;
}

export interface TaskIdentificationResult {
  task_type: string | null;
  domain: string | null;
  nature: string | null;
  method: string;
  matched_patterns: string[];
}

export interface NaturalLanguageParseResult {
  parsed_data: Record<string, any>;
  unstructured_notes: string[];
  warnings: string[];
}

export interface CategoryInfo {
  label: string;
  icon: string;
  description: string;
}

export interface CategoryData {
  label: string;
  icon: string;
  is_custom: boolean;
  instructions: string[];
}

export interface AddInstructionRequest {
  category: string;
  instruction: string;
  is_custom_category: boolean;
}

class KnowledgeBaseApi {
  private baseUrl = '/knowledge-base';
  private maxRetries = 3;
  private retryDelay = 1000; // Start with 1 second

  /**
   * Handle API errors consistently
   */
  private handleError(error: any, operation: string): never {
    console.error(`Knowledge Base API error in ${operation}:`, error);
    const message = error.response?.data?.detail || error.message || `Failed to ${operation}`;
    throw new Error(message);
  }

  /**
   * Retry logic for failed requests
   */
  private async retryRequest<T>(
    requestFn: () => Promise<T>,
    operation: string,
    retries: number = this.maxRetries
  ): Promise<T> {
    try {
      return await requestFn();
    } catch (error: any) {
      const status = error.response?.status;
      
      // Don't retry on client errors (4xx) or non-retryable server errors
      if (status >= 400 && status < 500) {
        throw error; // Client error - don't retry
      }
      
      // Only retry on specific server errors and network issues
      const retryableStatuses = [502, 503, 504, 522, 524]; // Gateway errors, service unavailable
      const isRetryable = !status || retryableStatuses.includes(status);
      
      if (isRetryable && retries > 0) {
        console.log(`Retrying ${operation}... (${this.maxRetries - retries + 1}/${this.maxRetries})`);
        await new Promise(resolve => setTimeout(resolve, this.retryDelay * (this.maxRetries - retries + 1)));
        return this.retryRequest(requestFn, operation, retries - 1);
      }
      
      throw error;
    }
  }

  /**
   * Get all configurations for a tenant
   */
  async getAllConfigs(): Promise<Record<string, KnowledgeBaseConfig[]>> {
    try {
      const response = await api.get(`${this.baseUrl}/configs`);
      return response.data;
    } catch (error) {
      this.handleError(error, 'get all configs');
    }
  }

  /**
   * Get available domains with metadata
   */
  async getAvailableDomains(): Promise<{
    success?: boolean;
    domains: Record<string, { 
      key?: string;
      display_name?: string;
      label?: string; 
      description?: string; 
      usage_count?: number; 
      custom?: boolean;
      has_rules?: boolean;
      framework_count?: number;
    }>;
    total_count?: number;
    standard_count?: number;
    custom_count?: number;
    total?: number;
  }> {
    try {
      const response = await api.get(`${this.baseUrl}/domains`);
      
      // Transform the response to match expected format for SafetyCheckCreator
      const domains = response.data.domains || {};
      const transformedDomains: Record<string, any> = {};
      
      for (const [key, value] of Object.entries(domains)) {
        const domainData = value as any;
        transformedDomains[key] = {
          key: key,
          display_name: domainData.label || domainData.display_name || key,
          has_rules: true, // Assume all returned domains have rules
          framework_count: domainData.usage_count || 0,
          ...domainData // Include original properties
        };
      }
      
      return {
        success: true,
        domains: transformedDomains,
        ...response.data
      };
    } catch (error) {
      this.handleError(error, 'get available domains');
    }
  }

  /**
   * Get a specific configuration
   */
  async getConfig(
    configType: string,
    configName: string
  ): Promise<KnowledgeBaseConfig> {
    try {
      const response = await api.get(
        `${this.baseUrl}/configs/${configType}/${configName}`
      );
      return response.data;
    } catch (error) {
      this.handleError(error, `get config ${configType}/${configName}`);
    }
  }

  /**
   * Update a configuration
   */
  async updateConfig(
    configType: string,
    configName: string,
    configData: Record<string, any>,
    modifiedBy?: string
  ): Promise<{ success: boolean; config: KnowledgeBaseConfig; message: string }> {
    try {
      const response = await api.put(
        `${this.baseUrl}/configs/${configType}/${configName}`,
        {
          config_data: configData,
          modified_by: modifiedBy,
        }
      );
      return response.data;
    } catch (error) {
      this.handleError(error, `update config ${configType}/${configName}`);
    }
  }

  /**
   * Get tenant profile
   */
  async getTenantProfile(): Promise<TenantProfile> {
    try {
      const response = await api.get(`${this.baseUrl}/profile`);
      return response.data;
    } catch (error) {
      this.handleError(error, 'get tenant profile');
    }
  }

  /**
   * Update tenant profile
   */
  async updateTenantProfile(
    profileData: Partial<TenantProfile>
  ): Promise<{ success: boolean; profile: TenantProfile; message: string }> {
    try {
      const response = await api.put(`${this.baseUrl}/profile`, {
        profile_data: profileData,
      });
      return response.data;
    } catch (error) {
      this.handleError(error, 'update tenant profile');
    }
  }

  /**
   * Get task history
   */
  async getTaskHistory(
    taskType?: string
  ): Promise<TaskHistory[]> {
    try {
      let url = `${this.baseUrl}/task-history`;
      if (taskType) {
        url += `?task_type=${taskType}`;
      }
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      this.handleError(error, 'get task history');
    }
  }

  /**
   * Record task execution
   */
  async recordTaskExecution(
    taskType: string,
    learnedPatterns?: any[],
    issues?: any[]
  ): Promise<{ success: boolean; task_history: TaskHistory; message: string }> {
    try {
      const response = await api.post(`${this.baseUrl}/task-history`, {
        task_type: taskType,
        learned_patterns: learnedPatterns,
        issues: issues,
      });
      return response.data;
    } catch (error) {
      this.handleError(error, 'record task execution');
    }
  }

  // Commented out - we only use universal safety checks now
  /*
  async createSafetyCheck(params: {
    description: string;
    name?: string;
    applicable_tasks?: string[];
    applicable_domains?: string[];
  }, tenantId: string = 'default'): Promise<{
    success: boolean;
    config: any;
    message: string;
  }> {
    try {
      const response = await api.post(`${this.baseUrl}/safety-checks?tenant_id=${tenantId}`, params);
      return response.data;
    } catch (error) {
      this.handleError(error, 'create safety check');
    }
  }

  async listSafetyChecks(
    includeInactive: boolean = false,
    tenantId: string = 'default'
  ): Promise<{
    success: boolean;
    safety_checks: any[];
    total: number;
  }> {
    try {
      const response = await api.get(
        `${this.baseUrl}/safety-checks?tenant_id=${tenantId}&include_inactive=${includeInactive}`
      );
      return response.data;
    } catch (error) {
      this.handleError(error, 'list safety checks');
    }
  }

  async getSafetyCheck(
    checkName: string,
    tenantId: string = 'default'
  ): Promise<{
    success: boolean;
    safety_check: any;
  }> {
    try {
      const response = await api.get(
        `${this.baseUrl}/safety-checks/${checkName}?tenant_id=${tenantId}`
      );
      return response.data;
    } catch (error) {
      this.handleError(error, 'get safety check');
    }
  }

  async updateSafetyCheck(
    checkName: string,
    updates: any,
    tenantId: string = 'default'
  ): Promise<{
    success: boolean;
    safety_check: any;
    message: string;
  }> {
    try {
      const response = await api.put(
        `${this.baseUrl}/safety-checks/${checkName}?tenant_id=${tenantId}`,
        updates
      );
      return response.data;
    } catch (error) {
      this.handleError(error, 'update safety check');
    }
  }

  async deleteSafetyCheck(
    checkName: string,
    tenantId: string = 'default'
  ): Promise<{
    success: boolean;
    message: string;
  }> {
    try {
      const response = await api.delete(
        `${this.baseUrl}/safety-checks/${checkName}?tenant_id=${tenantId}`
      );
      return response.data;
    } catch (error) {
      this.handleError(error, 'delete safety check');
    }
  }
  */


  /**
   * Onboard a new tenant
   */
  async onboardTenant(): Promise<{
    success: boolean;
    result: any;
    message: string;
  }> {
    try {
      const response = await api.post(`${this.baseUrl}/onboard`);
      return response.data;
    } catch (error) {
      this.handleError(error, 'onboard tenant');
    }
  }

  /**
   * Parse natural language to JSON
   */
  async parseNaturalLanguage(
    text: string,
    currentConfig?: Record<string, any>
  ): Promise<{ success: boolean } & NaturalLanguageParseResult> {
    try {
      const response = await api.post(`${this.baseUrl}/parse-natural`, {
        text,
        current_config: currentConfig,
      });
      return response.data;
    } catch (error) {
      this.handleError(error, 'parse natural language');
    }
  }

  /**
   * Convert JSON to natural language
   */
  async jsonToNaturalLanguage(
    configData: Record<string, any>
  ): Promise<{ success: boolean; natural_language?: string[]; categories?: Record<string, CategoryData> }> {
    try {
      const response = await api.post(`${this.baseUrl}/json-to-natural`, configData);
      return response.data;
    } catch (error) {
      this.handleError(error, 'convert JSON to natural language');
    }
  }

  /**
   * Identify task from message
   */
  async identifyTask(
    message: string,
    fileNames: string[] = []
  ): Promise<{ success: boolean } & TaskIdentificationResult> {
    try {
      const response = await api.post(`${this.baseUrl}/identify-task`, {
        message,
        file_names: fileNames,
      });
      return response.data;
    } catch (error) {
      this.handleError(error, 'identify task');
    }
  }

  /**
   * Get task pattern definitions
   */
  async getTaskPatterns(): Promise<{
    patterns: Record<string, any>;
    valid_task_types: string[];
    valid_domains: string[];
    valid_natures: string[];
  }> {
    try {
      const response = await api.get(`${this.baseUrl}/task-patterns`);
      return response.data;
    } catch (error) {
      this.handleError(error, 'get task patterns');
    }
  }

  /**
   * Get predefined categories
   */
  async getPredefinedCategories(): Promise<Record<string, CategoryInfo>> {
    try {
      const response = await api.get(`${this.baseUrl}/categories/predefined`);
      return response.data;
    } catch (error) {
      this.handleError(error, 'get predefined categories');
    }
  }

  /**
   * Add instruction to a category
   */
  async addInstruction(
    configType: string,
    configName: string,
    request: AddInstructionRequest
  ): Promise<{ success: boolean; config: KnowledgeBaseConfig; message: string }> {
    try {
      return await this.retryRequest(
        async () => {
          const response = await api.post(
            `${this.baseUrl}/configs/${configType}/${configName}/instruction`,
            request
          );
          return response.data;
        },
        'add instruction'
      );
    } catch (error) {
      this.handleError(error, 'add instruction');
    }
  }

  /**
   * Update an existing instruction
   */
  async updateInstruction(
    configType: string,
    configName: string,
    category: string,
    oldInstruction: string,
    newInstruction: string
  ): Promise<{ success: boolean; config: KnowledgeBaseConfig; message: string }> {
    try {
      return await this.retryRequest(
        async () => {
          const response = await api.put(
            `${this.baseUrl}/configs/${configType}/${configName}/instruction`,
            {
              category,
              old_instruction: oldInstruction,
              new_instruction: newInstruction
            }
          );
          return response.data;
        },
        'update instruction'
      );
    } catch (error) {
      this.handleError(error, 'update instruction');
    }
  }

  /**
   * Remove an instruction
   */
  async removeInstruction(
    configType: string,
    configName: string,
    category: string,
    instructionIndex: number
  ): Promise<{ success: boolean; config: KnowledgeBaseConfig; message: string }> {
    try {
      return await this.retryRequest(
        async () => {
          // Use query parameters instead of body for DELETE request
          const response = await api.delete(
            `${this.baseUrl}/configs/${configType}/${configName}/instruction?category=${encodeURIComponent(category)}&instruction_index=${instructionIndex}`
          );
          return response.data;
        },
        'remove instruction'
      );
    } catch (error) {
      this.handleError(error, 'remove instruction');
    }
  }

  /**
   * Reorder instructions within a category
   */
  async reorderInstructions(
    configType: string,
    configName: string,
    category: string,
    newOrder: number[]
  ): Promise<{ success: boolean; config: KnowledgeBaseConfig; message: string }> {
    try {
      return await this.retryRequest(
        async () => {
          const response = await api.post(
            `${this.baseUrl}/configs/${configType}/${configName}/reorder`,
            {
              category,
              new_order: newOrder
            }
          );
          return response.data;
        },
        'reorder instructions'
      );
    } catch (error) {
      this.handleError(error, 'reorder instructions');
    }
  }

  /**
   * Create a new configuration using AI from natural language
   */
  async createConfigWithAI(
    description: string,
    configType: string,
    configName: string
  ): Promise<{ success: boolean; config: any; message: string }> {
    try {
      return await this.retryRequest(
        async () => {
          const response = await api.post(
            `${this.baseUrl}/ai/create`,
            {
              description,
              config_type: configType,
              config_name: configName
            }
          );
          return response.data;
        },
        'create config with AI'
      );
    } catch (error) {
      this.handleError(error, 'create config with AI');
    }
  }

  /**
   * Preview an AI edit to a configuration (does not save)
   */
  async previewConfigEditWithAI(
    configType: string,
    configName: string,
    changeRequest: string
  ): Promise<{ 
    success: boolean; 
    preview_config: any; 
    original_config: any; 
    change_request: string;
    change_summary: string;
    message: string 
  }> {
    try {
      return await this.retryRequest(
        async () => {
          const response = await api.post(
            `${this.baseUrl}/ai/edit/${configType}/${configName}`,
            {
              change_request: changeRequest
            }
          );
          return response.data;
        },
        'preview config edit with AI'
      );
    } catch (error) {
      this.handleError(error, 'preview config edit with AI');
    }
  }

  /**
   * Accept and save a previewed AI edit
   */
  async acceptConfigEdit(
    configType: string,
    configName: string,
    modifiedConfig: any,
    changeRequest?: string,
    changeSummary?: string
  ): Promise<{ success: boolean; config: any; message: string }> {
    try {
      const response = await api.post(
        `${this.baseUrl}/configs/${configType}/${configName}/accept-edit`,
        {
          modified_config: modifiedConfig,
          change_request: changeRequest,
          change_summary: changeSummary
        }
      );
      return response.data;
    } catch (error) {
      this.handleError(error, 'accept config edit');
    }
  }

  /**
   * Edit an existing configuration using AI (backward compatibility - now uses preview flow)
   */
  async editConfigWithAI(
    configType: string,
    configName: string,
    changeRequest: string
  ): Promise<{ success: boolean; config: any; original_config: any; message: string }> {
    // For backward compatibility, return preview
    const preview = await this.previewConfigEditWithAI(configType, configName, changeRequest);
    return {
      success: preview.success,
      config: preview.preview_config,
      original_config: preview.original_config,
      message: preview.message
    };
  }

  /**
   * Get AI explanation for a configuration section
   */
  async explainConfigWithAI(
    configSection: any,
    sectionName?: string
  ): Promise<{ success: boolean; explanation: string }> {
    try {
      return await this.retryRequest(
        async () => {
          const response = await api.post(
            `${this.baseUrl}/ai/explain`,
            {
              config_section: configSection,
              section_name: sectionName
            }
          );
          return response.data;
        },
        'explain config with AI'
      );
    } catch (error) {
      this.handleError(error, 'explain config with AI');
    }
  }

  /**
   * Get version history for a configuration
   */
  async getVersionHistory(
    configType: string,
    configName: string
  ): Promise<{ success: boolean; versions: any[]; total: number }> {
    try {
      const response = await api.get(
        `${this.baseUrl}/configs/${configType}/${configName}/versions`
      );
      return response.data;
    } catch (error) {
      this.handleError(error, 'get version history');
    }
  }

  /**
   * Get a specific version of a configuration
   */
  async getSpecificVersion(
    configType: string,
    configName: string,
    version: number
  ): Promise<{ success: boolean; config: any }> {
    try {
      const response = await api.get(
        `${this.baseUrl}/configs/${configType}/${configName}/versions/${version}`
      );
      return response.data;
    } catch (error) {
      this.handleError(error, `get version ${version}`);
    }
  }

  /**
   * Restore an old version as a new version
   */
  async restoreVersion(
    configType: string,
    configName: string,
    version: number
  ): Promise<{ success: boolean; config: any; message: string }> {
    try {
      const response = await api.post(
        `${this.baseUrl}/configs/${configType}/${configName}/restore/${version}`
      );
      return response.data;
    } catch (error) {
      this.handleError(error, `restore version ${version}`);
    }
  }
}

export const knowledgeBaseApi = new KnowledgeBaseApi();